import React, { useEffect, useState, useRef } from 'react';
import { 
  fetchUserCarts, 
  updateCartItemQuantity, 
  createNewCart,
  moveCartItem,
  deleteCart,
  renameCart,       // We'll use this for the edit handler
  searchCarts,
  removeCartItem
} from '../api.js'; // Assuming createNewCart exists and works similarly
import { useNavigate } from 'react-router-dom';
import debounce from 'lodash/debounce';
import HighlightedText from '../components/HighlightedText'; // Keep this import
// Removed axios as we aim to use api.js functions primarily
// import axios from 'axios';
import Cookies from 'js-cookie';
// Removed unused styles import if it existed from CartPage.module.css
// import styles from './CartPage.module.css';
import authStyles from '../components/AuthForm.module.css';
// Re-added PencilSquareIcon
import { PlusIcon, TrashIcon, ArrowsUpDownIcon, PencilSquareIcon, XMarkIcon, ShoppingCartIcon } from '@heroicons/react/24/outline';
import vpStyles from './CartPageVision.module.css';

function CartPage() {
  const [carts, setCarts] = useState([]);
  const [newCartName, setNewCartName] = useState('');
  const [searchQuery, setSearchQuery] = useState(''); // Kept for search input
  // Removed selectedCarts as it wasn't used in the provided logic
  // const [selectedCarts, setSelectedCarts] = useState([]);
  const [editingCartName, setEditingCartName] = useState(''); // Keep this for inline input value
  const [popupImage, setPopupImage] = useState(null);
  // Keep drag state, but note functionality isn't implemented
  const [draggedItem, setDraggedItem] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  // Removed redundant moveItemModalOpen/setMoveItemModalOpen
  const [itemToMove, setItemToMove] = useState(null);
  const [targetCartId, setTargetCartId] = useState('');
  const [editingCartId, setEditingCartId] = useState(null); // <<< ADDED: ID of cart being edited inline
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const navigate = useNavigate();
  const authToken = Cookies.get('authToken'); // Keep authToken check
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [draggedItemInfo, setDraggedItemInfo] = useState(null); // { itemId: number, sourceCartId: number }
  const [isItemDragging, setIsItemDragging] = useState(false); // For styling the dragged item
  const inputRef = useRef(null); // <<< ADDED: Ref for focusing the input

  // --- API Function Handlers ---

  const handleDeleteCart = async (cartId) => { // Keep existing delete logic
    const confirmDelete = window.confirm('Are you sure you want to delete this cart?');
    if (!confirmDelete) return;
    try {
      // Assuming deleteCart returns a response object or throws on error
      const response = await deleteCart(cartId);
       // Check status if available, otherwise assume success if no error thrown
       // Adjust based on how your deleteCart API function behaves
      // if (response && (response.status === 200 || response.status === 204)) {
        setCarts(prevCarts => prevCarts.filter(cart => cart.cart_id !== cartId));
      // } else {
      //   throw new Error(response?.data?.message || 'Failed to delete cart');
      // }
    } catch (error) {
      console.error('Error deleting cart:', error);
      setError(`Failed to delete cart: ${error.message || 'Unknown error'}`);
    }
  };

  // --- Search ---
  // Debounced search function remains the same
  const debouncedSearch = debounce(async (query) => {
     setIsLoading(true);
    try {
      const results = await searchCarts(query); // Call API
      // Check the structure of 'results' here - is it the raw axios response or just the data?
      // If using apiClient directly, results might be the response object.
      const cartsData = results?.data; // <<<====== ENSURE THIS IS THE ARRAY
      if (Array.isArray(cartsData)) {
         console.log("Search Results Data:", cartsData); // Log the actual data
         // Sort search results too
         const sortedResults = cartsData.sort((a, b) => a.cart_name.localeCompare(b.cart_name));
         setCarts(sortedResults); // Update state with the array
      } else {
          console.warn("Search results did not contain expected data array:", results);
          setCarts([]); // Set empty if data is not an array
      }
    } catch (error) {
      console.error('Error searching carts:', error);
      setError('Failed to perform search.');
      setCarts([]); // Clear carts on search error
    } finally {
        setIsLoading(false);
    }
  }, 300);

  // useEffect for fetch/search remains the same
  useEffect(() => {
    const fetchInitialOrSearch = async () => {
      setIsLoading(true);
      setError('');
      if (!authToken) {
        setError('Authentication token not found. Please log in.');
        setIsLoading(false);
        return;
      }
      try {
        if (searchQuery.trim()) {
          await debouncedSearch(searchQuery.trim());
    } else {
          const response = await fetchUserCarts();
          const cartsData = response?.data; // <<<====== ENSURE THIS IS THE ARRAY
           if (Array.isArray(cartsData)) {
              const sortedCarts = cartsData.sort((a, b) => a.cart_name.localeCompare(b.cart_name));
              setCarts(sortedCarts);
           } else {
              console.warn("fetchUserCarts did not return expected data array:", response);
              setCarts([]);
           }
          }
        } catch (error) {
        // --- IMPROVED ERROR LOGGING ---
        console.error('Error fetching/searching cart data:', error);
        if (error.response) {
          // The request was made and the server responded with a status code
          // that falls out of the range of 2xx
          console.error("Error Response Data:", error.response.data);
          console.error("Error Response Status:", error.response.status);
          console.error("Error Response Headers:", error.response.headers);
           setError(`Failed to load cart data: Server responded with ${error.response.status}. ${error.response.data?.message || ''}`);
        } else if (error.request) {
          // The request was made but no response was received
          console.error("Error Request:", error.request);
          setError('Failed to load cart data: No response from server. Check connection or backend status.');
        } else {
          // Something happened in setting up the request that triggered an Error
          console.error('Error Message:', error.message);
          setError(`Failed to load cart data: ${error.message}`);
        }
        // --- END IMPROVED LOGGING ---
        setCarts([]); // Clear carts on error
      } finally {
        setIsLoading(false);
      }
    };

    fetchInitialOrSearch();
    return () => {
      debouncedSearch.cancel();
    };
  }, [searchQuery, authToken]); // Keep dependencies

  // --- Cart/Item Actions ---

  // Use createNewCart API function
  const handleCreateCart = async (e) => {
    e.preventDefault();
    if (!newCartName.trim()) return;
    try {
      // Use the imported API function
      const response = await createNewCart({ cartName: newCartName });
      // Add and re-sort
      const updatedCarts = [...carts, response.data].sort((a, b) => a.cart_name.localeCompare(b.cart_name));
      setCarts(updatedCarts);
      setShowCreateModal(false);
      setNewCartName('');
    } catch (err) {
      console.error('Error creating cart:', err);
      setError('Failed to create cart.');
    }
  };

  const handleDeleteItem = async (itemId) => { // Keep existing delete item logic
    try {
      console.log(`Deleting item with ID: ${itemId}`);
      await removeCartItem(itemId); // Use API function
      setCarts(prevCarts =>
        prevCarts.map(cart => ({
          ...cart,
          items: cart.items.filter(item => item.cart_item_id !== itemId)
        }))
      );
    } catch (error) {
      console.error('Error deleting item:', error);
       setError('Failed to delete item.');
    }
  };

  // handleQuantityChange remains the same (uses updateCartItemQuantity)
  const handleQuantityChange = async (cartId, itemId, newQuantity) => {
    // Basic validation
    const quantityNum = parseInt(newQuantity, 10);
    if (isNaN(quantityNum) || quantityNum < 1) {
        // Optionally reset to 1 or show error
        console.warn("Invalid quantity, setting to 1");
        newQuantity = 1;
        // Or: setError("Quantity must be at least 1"); return;
    }

    try {
      await updateCartItemQuantity(itemId, newQuantity);
      setCarts(prevCarts =>
        prevCarts.map(cart =>
          cart.cart_id === cartId
            ? {
                ...cart,
                items: cart.items.map(item =>
                  item.cart_item_id === itemId
                    ? { ...item, quantity: newQuantity } // Use validated/adjusted newQuantity
                    : item
                )
              }
            : cart
        )
      );
    } catch (error) {
      console.error('Error updating quantity:', error);
      setError('Failed to update quantity. Please try again.');
    }
  };


  // --- Drag and Drop Handlers ---

  const handleDragStart = (event, item, sourceCartId) => {
    console.log('Drag Start:', item);
    const itemInfo = {
      itemId: item.cart_item_id,
      sourceCartId: sourceCartId,
      // Include other item details if needed, but keep it minimal for dataTransfer
      name: item.name // Useful for potential visual feedback
    };
    // Use JSON string to store complex data easily
    event.dataTransfer.setData('application/json', JSON.stringify(itemInfo));
    event.dataTransfer.effectAllowed = "move";
    setDraggedItemInfo(itemInfo); // Store info in state for styling/logic
    setIsItemDragging(true); // Set flag for styling
    // Optional: Style the ghost image (browser default might be fine)
    // event.dataTransfer.setDragImage(event.target, 0, 0);
  };

  const handleDragEnd = (event) => {
    console.log('Drag End');
    // Clean up state regardless of drop success
    setDraggedItemInfo(null);
    setIsItemDragging(false);
    // Remove any lingering drag-over styles (handled in handleDragLeave/handleDrop)
     document.querySelectorAll(`.${vpStyles.dropTargetActive}`).forEach(el => {
        el.classList.remove(vpStyles.dropTargetActive);
     });
  };

  // --- Modified handleMoveItem (Now used by BOTH Drop and Modal) ---
  const performMoveItem = async (itemId, targetCartId) => {
    if (!itemId || !targetCartId) {
      console.error("Move cancelled: Missing item ID or target cart ID.");
      return; // Exit if essential data is missing
    }
    setIsLoading(true); // Indicate loading state
    setError(''); // Clear previous errors

    try {
      console.log(`Attempting to move item ${itemId} to cart ${targetCartId}`);
      // Use the existing API function
      await moveCartItem(itemId, targetCartId);

      // Update UI state *after* successful API call
      setCarts(prevCarts => {
        let itemToMove = null;
        let sourceCartIndex = -1;
        let targetCartIndex = -1;

        // Find item and indices
        prevCarts.forEach((cart, index) => {
          const itemIndex = cart.items.findIndex(item => item.cart_item_id === itemId);
          if (itemIndex > -1) {
            itemToMove = { ...cart.items[itemIndex], cart_id: parseInt(targetCartId) }; // Update cart_id on the item copy
            sourceCartIndex = index;
          }
          if (cart.cart_id === parseInt(targetCartId)) {
            targetCartIndex = index;
          }
        });

        if (!itemToMove || sourceCartIndex === -1 || targetCartIndex === -1) {
          console.error("Could not find item or carts in state for UI update.");
          return prevCarts; // Return original state if something went wrong
        }

        // Create new state array
        const updatedCarts = [...prevCarts];

        // Remove from source
        updatedCarts[sourceCartIndex] = {
          ...updatedCarts[sourceCartIndex],
          items: updatedCarts[sourceCartIndex].items.filter(item => item.cart_item_id !== itemId)
        };

        // Add to target
        updatedCarts[targetCartIndex] = {
          ...updatedCarts[targetCartIndex],
          items: [...updatedCarts[targetCartIndex].items, itemToMove] // Add the updated item
        };

         console.log("UI state updated after move.");
        return updatedCarts;
      });

      // Close the modal if it was open
      setShowMoveModal(false);
      setItemToMove(null);
      setTargetCartId('');

      } catch (error) {
        console.error('Error moving item:', error);
      setError(`Failed to move item: ${error.message || 'Unknown error'}`);
      // Optionally, re-select the original target in the modal if it failed?
    } finally {
       setIsLoading(false); // Stop loading indicator
       // Clean up drag state in case this was called from drop
       setDraggedItemInfo(null);
       setIsItemDragging(false);
       document.querySelectorAll(`.${vpStyles.dropTargetActive}`).forEach(el => {
          el.classList.remove(vpStyles.dropTargetActive);
       });
    }
  };

  // --- Drop Zone Handlers (for Cart Panels) ---

  const handleDragOver = (event, targetCartId) => {
    event.preventDefault(); // Necessary to allow dropping
    event.dataTransfer.dropEffect = "move";

    // Add visual cue if it's a valid drop target (different cart)
    if (draggedItemInfo && draggedItemInfo.sourceCartId !== targetCartId) {
        event.currentTarget.classList.add(vpStyles.dropTargetActive);
    }
  };

   const handleDragLeave = (event) => {
        // Remove visual cue when dragging out
        event.currentTarget.classList.remove(vpStyles.dropTargetActive);
   };

  const handleDrop = (event, targetCartId) => {
    event.preventDefault(); // Prevent default drop behavior (like opening link)
     event.currentTarget.classList.remove(vpStyles.dropTargetActive); // Clean up style

    try {
        const itemInfoString = event.dataTransfer.getData('application/json');
        if (!itemInfoString) {
            console.warn("Drop failed: No data transferred.");
            return;
        }
        const itemInfo = JSON.parse(itemInfoString);
        console.log('Drop Event - Target Cart ID:', targetCartId, 'Dropped Item Info:', itemInfo);


        // Check if dropping onto the same cart
        if (itemInfo.sourceCartId === targetCartId) {
          console.log("Drop cancelled: Item dropped onto its original cart.");
          return; // Do nothing if dropped on the same cart
        }

        // Call the consolidated move function
        performMoveItem(itemInfo.itemId, targetCartId);

    } catch (e) {
        console.error("Error processing drop event:", e);
         setError("An error occurred during the drop operation.");
    } finally {
         // Final cleanup of drag state handled in performMoveItem or dragEnd
    }
  };


  // --- Modal Specific Handlers ---
  const openMoveItemModal = (item) => { // Keep this as is
    setItemToMove(item);
    setTargetCartId(''); // Clear previous selection
    setShowMoveModal(true);
  };

  // This function is called ONLY by the modal's form submit
  const handleMoveItemSubmit = (e) => {
     e.preventDefault(); // Prevent default form submission
     if (itemToMove && targetCartId) {
       performMoveItem(itemToMove.cart_item_id, targetCartId);
     } else {
        console.warn("Move modal submit cancelled: No item or target selected.");
        setError("Please select a destination cart."); // User feedback
     }
  };

  // handleCheckoutCart remains the same
  const handleCheckoutCart = (cartIdToCheckout) => {
    const cartToCheckout = carts.find(cart => cart.cart_id === cartIdToCheckout);
    if (!cartToCheckout || cartToCheckout.items.length === 0) {
      setError("Cannot checkout an empty cart.");
      // Optionally clear the error after a few seconds
      // setTimeout(() => setError(''), 3000);
      return;
    }
    // Pass the cart ID as an array in the state, matching CheckoutPage expectation
    navigate('/checkout', { state: { cartIds: [cartIdToCheckout] } }); // <<< MODIFIED: Pass cartIds array
  };

  // handleImageClick remains the same (corrected name usage)
  const handleImageClick = (imageUrl) => setPopupImage(imageUrl);
  const handleClosePopup = () => setPopupImage(null);

  // --- Inline Edit Handlers ---

  // Start editing a cart name inline
  const handleStartEdit = (cart) => {
    setEditingCartId(cart.cart_id);
    setEditingCartName(cart.cart_name);
    // Focus the input field after state update allows it to render
    // Use a slight delay to ensure the input is in the DOM
    setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select(); // Optional: select text on focus
    }, 0);
  };

  // Cancel inline editing
  const handleCancelEdit = () => {
    setEditingCartId(null);
    setEditingCartName('');
    setError(''); // Clear any potential previous edit error
  };

  // Save the inline edit
  const handleSaveEdit = async (cartId) => {
    const newName = editingCartName.trim();
    if (!newName) {
      setError("Cart name cannot be empty."); // Show inline error potentially
      return;
    }
    // Optional: Check if name actually changed
    const originalCart = carts.find(c => c.cart_id === cartId);
    if (originalCart && originalCart.cart_name === newName) {
        handleCancelEdit(); // Name didn't change, just cancel edit mode
        return;
    }


    setIsLoading(true); // Indicate saving...
    setError('');
    try {
      await renameCart(cartId, newName); // Use API function
      setCarts(prevCarts =>
        prevCarts.map(cart =>
          cart.cart_id === cartId
            ? { ...cart, cart_name: newName }
            : cart
        )
      );
      handleCancelEdit(); // Exit edit mode on success
    } catch (error) {
      console.error('Error updating cart name inline:', error);
      setError('Failed to save cart name. Please try again.');
      // Keep editing mode active so user can retry or cancel
    } finally {
       setIsLoading(false);
    }
  };

  // Handle key presses within the inline input
  const handleEditKeyDown = (event, cartId) => {
    if (event.key === 'Enter') {
      event.preventDefault(); // Prevent form submission if it were in a form
      handleSaveEdit(cartId);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      handleCancelEdit();
    }
  };

  // --- Render Logic ---
  // ... (Loading and Error states remain the same) ...
  if (isLoading && carts.length === 0) { // Show loading only if no carts are displayed yet
    return <div className="flex justify-center items-center min-h-screen text-gray-500">Loading...</div>;
  }
   if (error && carts.length === 0 && !isLoading) { // Show full page error if loading finished with error and no carts
      return <div className={`${vpStyles.emptyStateContainer} text-red-600`}>{error}</div>;
   }


  return (
    <div className={`container mx-auto px-4 sm:px-6 lg:px-8 py-12 ${vpStyles.pageContainer}`}>
      <h1 className={`${vpStyles.pageTitle} text-3xl md:text-4xl font-semibold text-center mb-12`}>
        Your Carts
      </h1>

      {error && carts.length > 0 && (
          <div className={`${authStyles.errorMessage} text-sm mb-8 max-w-xl mx-auto`}>{error}</div>
      )}

      {/* Controls Container */}
      <div className={`flex flex-col sm:flex-row items-center justify-center gap-4 mb-10 ${vpStyles.controlsContainer}`}>
        {/* Create Cart Button */}
        <button
          onClick={() => setShowCreateModal(true)}
          className={`${vpStyles.primaryButton} inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold text-white shadow-lg w-full sm:w-auto`}
        >
          <PlusIcon className="h-5 w-5" />
          Create New Cart
        </button>

        {/* Re-add Search Input */}
        <div className="relative w-full sm:w-auto sm:flex-grow max-w-xs">
          <input
            type="text"
              placeholder="Search carts & items..."
            value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)} // Connect to state setter
              className={`block w-full appearance-none rounded-full border bg-white/80 px-4 py-2.5 pl-10 text-gray-900 placeholder-gray-500 focus:z-10 focus:outline-none sm:text-sm ${authStyles.inputField}`} // Use authStyles.inputField
          />
           <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
             {/* Optional: Search Icon */}
             <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5"><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" /></svg>
          </span>
        </div>
      </div>

      {carts.length === 0 && !isLoading ? (
        <div className={vpStyles.emptyStateContainer}>
            <ShoppingCartIcon className={`h-20 w-20 mx-auto text-gray-300 mb-6 ${vpStyles.emptyStateIcon}`} />
            <p className={`text-lg text-gray-500 mb-8 ${vpStyles.emptyStateText}`}>
                {searchQuery ? 'No carts or items match your search.' : 'Your shopping space is empty. Create a cart to start collecting items!'}
            </p>
        </div>
      ) : (
        <div className="space-y-10">
      {carts.map((cart) => (
        <div 
          key={cart.cart_id} 
              className={vpStyles.cartPanel}
              onDragOver={(e) => handleDragOver(e, cart.cart_id)}
              onDragLeave={handleDragLeave} // Add drag leave to remove highlight
              onDrop={(e) => handleDrop(e, cart.cart_id)}
              data-cart-id={cart.cart_id} // Keep for reference if needed elsewhere
            >
                {/* Panel Header */}
                <div className={`${vpStyles.panelHeader} flex justify-between items-center gap-4 p-5 md:p-6`}>
                  {editingCartId === cart.cart_id ? (
                    // --- INLINE EDITING VIEW ---
                    <div className="flex-grow flex items-center gap-2">
              <input
                        ref={inputRef} // Assign ref
                    type="text"
                        value={editingCartName}
                        onChange={(e) => setEditingCartName(e.target.value)}
                        onKeyDown={(e) => handleEditKeyDown(e, cart.cart_id)}
                        className={`flex-grow appearance-none rounded-md border border-indigo-300 bg-white px-3 py-1.5 text-lg md:text-xl font-semibold shadow-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 ${authStyles.inputField}`}
                      />
                      {/* Inline Save/Cancel Buttons */}
                      <button
                        onClick={() => handleSaveEdit(cart.cart_id)}
                        title="Save name"
                        className="p-1.5 rounded text-green-600 hover:bg-green-100"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
                      </button>
                  <button
                        onClick={handleCancelEdit}
                        title="Cancel edit"
                        className="p-1.5 rounded text-red-600 hover:bg-red-100"
                  >
                        <XMarkIcon className="w-5 h-5" />
                  </button>
                </div>
              ) : (
                    // --- DEFAULT DISPLAY VIEW ---
                    <>
                      <h2 className={`${vpStyles.panelTitle} text-lg md:text-xl font-semibold flex-grow truncate`}>
                        <HighlightedText text={cart.cart_name} highlight={searchQuery} />
                      </h2>
                      <div className={`${vpStyles.panelActions} flex items-center gap-1 flex-shrink-0`}>
                        {/* Edit Button starts inline edit */}
              <button
                          onClick={() => handleStartEdit(cart)} // <<< MODIFIED
                          title="Edit cart name"
                          className="p-2 rounded-full text-gray-500 hover:bg-gray-100"
              >
                          <PencilSquareIcon className="h-5 w-5" />
              </button>
              <button
                onClick={() => handleDeleteCart(cart.cart_id)}
                          title="Delete cart"
                          className="p-2 rounded-full text-gray-500 hover:bg-gray-100"
              >
                          <TrashIcon className="h-5 w-5" />
              </button>
            </div>
                    </>
                  )}
          </div>
                {/* Panel Items */}
                 <div className={`${vpStyles.itemsList} p-4 md:p-6`}>
                    {cart.items.length === 0 ? (
                        <p className="text-gray-500 text-center py-6">Nothing in this cart yet.</p>
                    ) : (
                        <ul className={`${vpStyles.itemList} divide-y divide-gray-200/50 px-5 md:px-6 pb-5 md:pb-6`}>
          {cart.items.map((item) => (
                            <li
                              key={item.cart_item_id}
                              className={`${vpStyles.listItem} py-4 flex items-center justify-between gap-4 ${isItemDragging && draggedItemInfo?.itemId === item.cart_item_id ? vpStyles.draggingItem : ''}`}
                              draggable={true} // <<< MAKE ITEM DRAGGABLE
                              onDragStart={(e) => handleDragStart(e, item, cart.cart_id)} // <<< ADD DRAG START
                              onDragEnd={handleDragEnd} // <<< ADD DRAG END
                            >
                              <div className="flex items-center flex-grow min-w-0"> {/* Allow text truncation */}
                                <img
                                    src={item.image_url || 'https://via.placeholder.com/100?text=No+Image'}
                  alt={item.name} 
                                    className={`${vpStyles.itemImage} h-16 w-16 rounded-lg object-cover mr-4 flex-shrink-0 cursor-pointer`}
                                    onClick={() => handleImageClick(item.image_url)}
                                    onError={(e) => { e.target.onerror = null; e.target.src='https://via.placeholder.com/100?text=No+Image'; }} // Fallback
                                />
                                <div className="min-w-0"> {/* Container for text that might overflow */}
                                   {/* Use HighlightedText for Item Name if searching */}
                                   <h3 className={`${vpStyles.itemName} text-base font-medium truncate`}>
                                      <HighlightedText text={item.name || 'Unknown Item'} highlight={searchQuery} />
                                   </h3>
                                  <p className="text-xs text-gray-500 mt-0.5">
                                    Size: {item.size || 'N/A'}
                                  </p>
                                  <p className={`${vpStyles.itemPrice} text-sm text-gray-600`}>${parseFloat(item.price || 0).toFixed(2)}</p>
                                   {/* Quantity Input */}
                                   <div className="mt-1 flex items-center">
                                      <label htmlFor={`quantity-${item.cart_item_id}`} className="text-xs text-gray-500 mr-1.5">Qty:</label>
                                      <input
                                          type="number"
                                          id={`quantity-${item.cart_item_id}`}
                                          min="1"
                                          value={item.quantity}
                                          onChange={(e) => handleQuantityChange(cart.cart_id, item.cart_item_id, e.target.value)}
                                          className={`w-16 appearance-none rounded border px-2 py-1 text-sm ${authStyles.inputField} focus:ring-0 focus:outline-none`} // Basic styling
                />
              </div>
                                  {item.vendor && <p className={`${vpStyles.itemVendor} text-xs text-gray-500 mt-1 truncate`}>From: {item.vendor}</p>}
              </div>
              </div>
                               <div className={`flex flex-shrink-0 items-center gap-2 ml-4 ${vpStyles.itemActions}`}>
                                  {/* Move Button (triggers modal) */}
                <button 
                                    onClick={() => openMoveItemModal(item)} // KEEP THIS
                                    title="Move Item"
                                    className={`${vpStyles.itemActionButton} ${vpStyles.itemMoveButton} flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium`}
                >
                                    <ArrowsUpDownIcon className="h-4 w-4" />
                                    <span className="hidden sm:inline">Move</span>
                </button>
                                  {/* Delete Item Button */}
                <button
                                   onClick={() => handleDeleteItem(item.cart_item_id)} // Keep this
                                   title="Delete Item"
                                    className={`${vpStyles.itemActionButton} ${vpStyles.itemDeleteButton} flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium`}
                >
                                   <TrashIcon className="h-4 w-4" />
                                   <span className="hidden sm:inline">Remove</span>
                </button>
              </div>
                            </li>
                          ))}
                        </ul>
                    )}
            </div>
                {/* Panel Footer - Checkout */}
                {cart.items.length > 0 && (
                    <div className={`${vpStyles.panelFooter} p-4 md:p-5 text-right`}>
            <button
              onClick={() => handleCheckoutCart(cart.cart_id)}
                        className={`${vpStyles.panelCheckoutButton} inline-flex items-center px-5 py-2 rounded-full text-sm font-semibold`}
            >
                        Checkout Cart
            </button>
          </div>
                )}
            </div>
            ))}
        </div>
      )}

      {/* --- Modals --- */}
      {/* Create Cart Modal (using createNewCart via handleCreateCart) */}
      {showCreateModal && ( /* ... Modal JSX ... */
         <div className={vpStyles.cartModalBackdrop} onClick={() => setShowCreateModal(false)}>
          <div className={`${vpStyles.cartModalContent} w-full max-w-md p-6 md:p-8`} onClick={(e) => e.stopPropagation()}>
            <h3 className={`${authStyles.modalTitle} text-xl font-semibold mb-6`}>Create New Cart</h3>
            <button onClick={() => setShowCreateModal(false)} className={`${vpStyles.cartModalCloseButton} p-1.5 rounded-full bg-gray-200/60 hover:bg-gray-300/80 text-gray-500 hover:text-gray-800`}>
                <XMarkIcon className="h-5 w-5" />
            </button>
            <form onSubmit={handleCreateCart} className="space-y-5">
              <div>
                <label htmlFor="newCartName" className={`block text-sm font-medium text-gray-700 mb-1 ${authStyles.inputLabel}`}>
                  Cart Name
                </label>
                <input
                  type="text" id="newCartName" value={newCartName}
                  onChange={(e) => setNewCartName(e.target.value)} required
                  className={`w-full appearance-none rounded-lg border px-3 py-2.5 ${authStyles.inputField}`}
                  placeholder="e.g., Birthday Gifts"
                />
              </div>
              <button type="submit" className={`w-full justify-center rounded-lg py-2.5 px-4 text-sm font-semibold ${authStyles.primaryButton} bg-blue-600 text-white hover:bg-blue-700`}>
                Create Cart
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Move Item Modal (using moveCartItem via handleMoveItemSubmit) */}
       {showMoveModal && itemToMove && (
         <div className={vpStyles.cartModalBackdrop} onClick={() => setShowMoveModal(false)}>
           <div className={`${vpStyles.cartModalContent} w-full max-w-md p-6 md:p-8`} onClick={(e) => e.stopPropagation()}>
             {/* ... modal header and close button ... */}
              <p className="text-sm text-gray-600 mb-5">Move "<span className="font-medium">{itemToMove.name}</span>" to:</p>
              {/* Use handleMoveItemSubmit for the form */}
             <form onSubmit={handleMoveItemSubmit} className="space-y-5">
               <div>
                  <label htmlFor="targetCartId" className="sr-only">Select Cart</label>
                  <select
                    id="targetCartId" value={targetCartId}
                    onChange={(e) => setTargetCartId(e.target.value)} required
                    className={`w-full appearance-none rounded-lg border px-3 py-2.5 bg-white ${authStyles.inputField}`}
                  >
                     <option value="" disabled>-- Select destination --</option>
                     {/* Filter out the item's *current* cart */}
                     {carts.filter(cart => cart.cart_id !== itemToMove.cart_id).map(cart => (
                        <option key={cart.cart_id} value={cart.cart_id}>
                          {cart.cart_name}
                        </option>
                     ))}
                  </select>
               </div>
                <button type="submit" className={`w-full justify-center rounded-lg py-2.5 px-4 text-sm font-semibold ${authStyles.primaryButton} bg-indigo-600 text-white hover:bg-indigo-700`}>
                  Move Item
                  </button>
             </form>
           </div>
            </div>
       )}

      {/* Image Popup Modal */}
      {popupImage && ( /* ... Modal JSX ... */
        <div className={`${vpStyles.cartModalBackdrop}`} onClick={handleClosePopup}>
          <div className={`${vpStyles.cartModalContent} ${vpStyles.imagePopupContent}`} onClick={(e) => e.stopPropagation()}>
             <button onClick={handleClosePopup} className={`${vpStyles.cartModalCloseButton} ${vpStyles.imagePopupCloseButton}`}>
               <XMarkIcon className="h-6 w-6" />
              </button>
             <img src={popupImage} alt="Enlarged view" className={vpStyles.imagePopupImage}/>
          </div>
        </div>
      )}

    </div> // End page container
  );
}

export default CartPage;